import { useQuery, type QueryClient, type useQueryClient } from '@tanstack/react-query';
import type { ProjectRecentUpdatesResult } from '@task-tracker/shared';
import { projectActivityKeys, projectRecentUpdatesKeys } from '@task-tracker/shared';
import { apiClient } from '../lib/api-client';

// The key factory used to be defined here and byte-identically in mobile; it
// now lives in @task-tracker/shared. Re-exported so existing imports still work.
export { projectRecentUpdatesKeys };

type QueryClientLike = QueryClient | ReturnType<typeof useQueryClient>;

/** Invalidates BOTH project feeds — the Recent Updates tab and the merged
 *  Notifications tab render the same audit rows, so refreshing one without the
 *  other leaves the tabs disagreeing. */
export function invalidateProjectRecentUpdates(
  queryClient: QueryClientLike,
  projectId: string | null | undefined,
) {
  if (!projectId) return;
  void queryClient.invalidateQueries({ queryKey: projectRecentUpdatesKeys.list(projectId) });
  void queryClient.invalidateQueries({ queryKey: projectActivityKeys.list(projectId) });
}

export function useProjectRecentUpdates(projectId: string, limit = 20) {
  return useQuery({
    queryKey: projectRecentUpdatesKeys.page(projectId, limit),
    queryFn: () =>
      apiClient<ProjectRecentUpdatesResult>(
        `/api/v1/projects/${projectId}/recent-updates?page=1&limit=${limit}`,
      ).then((response) => response.data),
    enabled: !!projectId,
  });
}
